import { SignalHigh, SignalLow, SignalMedium, TriangleAlert, type LucideIcon } from 'lucide-react';
import { TASK_PRIORITY_LABELS, type TaskDetail, type TaskPriority, type TaskUpdateInput } from '@shared/types/tasks';
import { TASK_SOURCE_LABELS } from '@shared/types/task-sources';
import { StatusSelect } from '@/components/StatusSelect';
import { statusToneClasses } from '@/components/status-tone';
import { cn } from '@/lib/utils';
import { NO_PRIORITY, TASK_PRIORITY_STATUSES } from './task-types';

/**
 * SF-T-151: a task's priority, Urgent to Low. On a card it's one small icon (signal bars, a warning
 * triangle for Urgent) so it adds no chip to a busy card; on the task it's a StatusSelect.
 */

const ICONS: Record<TaskPriority, LucideIcon> = { urgent: TriangleAlert, high: SignalHigh, medium: SignalMedium, low: SignalLow };

/** The priority's icon in its tone, for a card; its name is the hint and the label. */
export function PriorityIcon({ priority, className }: { priority: TaskPriority; className?: string }) {
  const Icon = ICONS[priority];
  const name = `${TASK_PRIORITY_LABELS[priority]} priority`;
  return (
    <span className={cn('inline-flex items-center', statusToneClasses(TASK_PRIORITY_STATUSES[priority].tone).text, className)} data-hint={name} role="img" aria-label={name}>
      <Icon className="size-3.5" strokeWidth={2.75} aria-hidden />
    </span>
  );
}

type Patch = (input: TaskUpdateInput, optimistic?: Partial<TaskDetail>) => Promise<boolean>;

/**
 * Urgent to Low, or none, on the task. Read-only on a mirrored task whose source maps priority: it's
 * set there, and the hint says where.
 */
export function PrioritySelect({ task, patch }: { task: TaskDetail; patch: Patch }) {
  const synced = Boolean(task.upstream?.priority_synced);
  const value = task.priority ?? NO_PRIORITY;
  if (synced) {
    const where = task.upstream ? TASK_SOURCE_LABELS[task.upstream.kind] : 'its source';
    return (
      <span data-hint={`Set in ${where}: the priority comes from there`}>
        <StatusSelect value={value} statuses={TASK_PRIORITY_STATUSES} readOnly onValueChange={() => undefined} />
      </span>
    );
  }
  return (
    <StatusSelect
      value={value}
      statuses={TASK_PRIORITY_STATUSES}
      aria-label="Priority"
      onValueChange={(v) => {
        const priority = v === NO_PRIORITY ? null : (v as TaskPriority);
        void patch({ priority }, { priority });
      }}
    />
  );
}
